// Utility wrapper for the WebAssembly zstd decompressor that was generated by Emscripten.
// This file lazily initialises the WASM module the first time it is needed and exposes a
// simple Promise-based API around it.

let wasmModulePromise;

/**
 * Lazily import and initialise the decompressor WASM module.
 * The underlying `decompressor_module.js` file was generated by Emscripten in
 * `output_wasm` and uses the global `Module` variable pattern.  When imported as
 * an ES module, it returns that Module object.  We wait for `onRuntimeInitialized`
 * before resolving so callers can safely use the exported C symbols.
 */
export function initDecompressorModule() {
  if (wasmModulePromise) return wasmModulePromise;

  wasmModulePromise = new Promise(async (resolve, reject) => {
    try {
      // NOTE: The relative path below is resolved from the location of this file.
      // Adjust if you move files around.
      const moduleLoader = await import('../wasm/decompressor_module.js');
      const Module = moduleLoader.default || moduleLoader;

      // Configure Module for browser environment
      const moduleConfig = {
        ...Module,
        // Override environment detection to force browser mode
        ENVIRONMENT: 'web',
        // Provide stubs for Node.js modules
        nodeFS: null,
        nodePath: null
      };

      // Initialize the module
      const initializedModule = await Module(moduleConfig);

      // In rare cases on some bundlers the import may already be ready. Guard for both.
      if (initializedModule.calledRun || initializedModule.onRuntimeInitialized == null) {
        resolve(initializedModule);
        return;
      }

      initializedModule.onRuntimeInitialized = () => resolve(initializedModule);
    } catch (err) {
      reject(err);
    }
  });

  return wasmModulePromise;
}

/**
 * Decompress a Uint8Array containing zstd-compressed bytes into a new Uint8Array.
 * Throws on non-zero return codes from the underlying C function.
 */
export async function decompressUint8Array(compressedU8) {
  const Module = await initDecompressorModule();

  const inputPtr = Module._malloc(compressedU8.length);
  
  // Copy input data to WASM memory using setValue
  for (let i = 0; i < compressedU8.length; i++) {
    Module.setValue(inputPtr + i, compressedU8[i], 'i8');
  }

  // Allocate space for the two output pointers (uint8_t* outPtr, uint32_t outLen)
  const outPtrPtr = Module._malloc(4);
  const outLenPtr = Module._malloc(4);

  const result = Module._decompress_zstd(inputPtr, compressedU8.length, outPtrPtr, outLenPtr);
  if (result !== 0) {
    Module._free(inputPtr);
    Module._free(outPtrPtr);
    Module._free(outLenPtr);
    throw new Error(`WASM zstd decompression failed with code ${result}`);
  }

  const outPtr = Module.getValue(outPtrPtr, '*');
  const outLen = Module.getValue(outLenPtr, 'i32');

  const outBytes = new Uint8Array(outLen);
  
  // Copy output data from WASM memory using getValue
  for (let i = 0; i < outLen; i++) {
    outBytes[i] = Module.getValue(outPtr + i, 'i8');
  }

  // Clean-up WASM memory
  Module._free(inputPtr);
  Module._free(outPtrPtr);
  Module._free(outLenPtr);
  Module._free_buffer(outPtr); // custom helper exposed by the module

  return outBytes;
}

/**
 * Convenience helper that returns the decompressed data as a UTF-8 string.
 */
export async function decompressToString(compressedU8) {
  const bytes = await decompressUint8Array(compressedU8);
  return new TextDecoder('utf-8').decode(bytes);
}
